import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, MapPin, Calendar, Phone, Mail, Trophy, AlertOctagon, PauseCircle, ArrowRight, FileText, Image as ImageIcon, Download, Pencil } from "lucide-react";
import { STAGE_LABEL, STAGE_COLOR, SALES_STAGES, fmtCurrency } from "@/lib/sales";
import MeetingModal from "@/components/sales/MeetingModal";
import MeetingMomModal from "@/components/sales/MeetingMomModal";
import WonGateModal from "@/components/sales/WonGateModal";
import LossModal from "@/components/sales/LossModal";
import EditSalesLeadDialog from "@/components/sales/EditSalesLeadDialog";
import { useAuth } from "@/context/AuthContext";
import { isSalesManagerRole, isSalesExecutiveRole } from "@/lib/roleAliases";

export default function LeadDetailDrawer({ lead, onClose, onChanged, executives = [] }) {
  const { roleName, isAdmin, roleType } = useAuth();
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [momOpen, setMomOpen] = useState(false);
  const [wonOpen, setWonOpen] = useState(false);
  const [lossOpen, setLossOpen] = useState(false);

  if (!lead) return null;

  const isMgrOrUp = isAdmin || roleType === "director" || isSalesManagerRole(roleName);
  const canEdit = isMgrOrUp || isSalesExecutiveRole(roleName);

  const meetingHasElapsed = (() => {
    const m = lead.meeting;
    if (!m?.date || !m?.time) return false;
    try {
      const dt = new Date(`${m.date}T${m.time}:00`);
      return dt <= new Date();
    } catch { return false; }
  })();

  const changeStatus = async (status) => {
    try {
      await api.patch(`/sales/leads/${lead.id}/status`, { status });
      toast.success(`Moved to ${STAGE_LABEL[status]}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const assignExec = async (assigned_to_user_id) => {
    try {
      await api.post(`/sales/leads/${lead.id}/assign`, { assigned_to_user_id });
      toast.success("Assignment updated");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const orderedStages = ["new","contacted","meeting_scheduled","in_discussion","pipeline"];
  const currentIdx = orderedStages.indexOf(lead.status);

  return (
    <>
      <div className="fixed inset-0 z-40 flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="lead-drawer">
        <div className="w-full max-w-lg h-full bg-[#0a0a0a] border-l border-[#262626] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="p-6 border-b border-[#262626] flex items-start justify-between">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Lead</p>
              <h3 className="font-display text-2xl text-white mt-1">{lead.client_name}</h3>
              <div className="flex items-center gap-3 mt-2 text-xs text-gray-400 font-mono">
                <span className="flex items-center gap-1"><Phone className="w-3 h-3"/>{lead.phone}</span>
                {lead.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3"/>{lead.email}</span>}
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4"/></button>
          </div>

          <div className="p-6 space-y-6">
            {/* Current status */}
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-2">Current Status</p>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: STAGE_COLOR[lead.status] }}/>
                  <span className="text-white font-medium">{STAGE_LABEL[lead.status]}</span>
                </div>
              </div>
              {canEdit && (
                <button
                  onClick={() => setEditOpen(true)}
                  className="iz-btn-secondary text-xs flex items-center gap-1.5"
                  data-testid="lead-drawer-edit"
                  title={lead.status === "won"
                    ? "Correct client details or Won attachments"
                    : "Edit client contact / project details"}
                >
                  <Pencil className="w-3 h-3"/>
                  {lead.status === "won" ? "Edit Won Details" : "Edit"}
                </button>
              )}
            </div>

            {/* Stage stepper (for pre-win stages) */}
            {currentIdx >= 0 && (
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-3">Pipeline progress</p>
                <div className="flex items-center gap-1.5">
                  {orderedStages.map((s, i) => (
                    <div key={s} className={`flex-1 h-1.5 rounded-full ${i <= currentIdx ? "bg-[#d4af37]" : "bg-[#1a1a1a]"}`}/>
                  ))}
                </div>
                <div className="flex justify-between text-[9px] font-mono uppercase tracking-widest text-gray-500 mt-2">
                  {orderedStages.map(s => <span key={s} className={`${lead.status === s ? "text-[#d4af37]" : ""}`}>{STAGE_LABEL[s].split(" ")[0]}</span>)}
                </div>
              </div>
            )}

            {/* Assignment */}
            {isMgrOrUp && (
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-2">Assign To</p>
                <select
                  value={lead.assigned_to_user_id || ""}
                  onChange={(e) => assignExec(e.target.value || null)}
                  className="iz-input"
                  data-testid="drawer-assign-select"
                >
                  <option value="">Unassigned</option>
                  {executives.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
            )}

            {/* Actions */}
            {canEdit && (
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-3">Advance Pipeline</p>
                <div className="grid grid-cols-2 gap-2">
                  {lead.status === "new" && (
                    <button onClick={() => changeStatus("contacted")} className="iz-btn-secondary text-xs flex items-center justify-center gap-1" data-testid="advance-contacted">
                      <ArrowRight className="w-3 h-3"/>Contacted
                    </button>
                  )}
                  {["new","contacted"].includes(lead.status) && (
                    <button onClick={() => setMeetingOpen(true)} className="iz-btn-primary text-xs flex items-center justify-center gap-1" data-testid="advance-meeting">
                      <Calendar className="w-3 h-3"/>Schedule Meeting
                    </button>
                  )}
                  {lead.status === "meeting_scheduled" && (
                    <>
                      <button onClick={() => setMeetingOpen(true)} className="iz-btn-secondary text-xs" data-testid="advance-reschedule">Reschedule</button>
                      {meetingHasElapsed && (
                        <button onClick={() => setMomOpen(true)} className="iz-btn-primary text-xs" data-testid="advance-mom">Log MOM</button>
                      )}
                      <button onClick={() => changeStatus("in_discussion")} className="iz-btn-secondary text-xs" data-testid="advance-discussion">→ In Discussion</button>
                    </>
                  )}
                  {lead.status === "in_discussion" && (
                    <button onClick={() => changeStatus("pipeline")} className="iz-btn-primary text-xs" data-testid="advance-pipeline">→ Pipeline</button>
                  )}
                  {["pipeline","in_discussion","meeting_scheduled"].includes(lead.status) && (
                    <button onClick={() => setWonOpen(true)} className="iz-btn-primary text-xs flex items-center justify-center gap-1" data-testid="advance-won">
                      <Trophy className="w-3 h-3"/>Close as Won
                    </button>
                  )}
                  {!["won","loss"].includes(lead.status) && (
                    <>
                      <button onClick={() => setLossOpen(true)} className="iz-btn-danger text-xs flex items-center justify-center gap-1" data-testid="advance-loss">
                        <AlertOctagon className="w-3 h-3"/>Mark Loss
                      </button>
                      {lead.status !== "hold" && (
                        <button onClick={() => changeStatus("hold")} className="iz-btn-secondary text-xs flex items-center justify-center gap-1" data-testid="advance-hold">
                          <PauseCircle className="w-3 h-3"/>Hold / Nurture
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Meeting info */}
            {lead.meeting && (
              <div className="iz-card p-4">
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">Meeting</p>
                <div className="text-sm text-white flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#d4af37]"/> {lead.meeting.date} · {lead.meeting.time}
                </div>
                {(lead.meeting.location_text || lead.meeting.location_lat) && (
                  <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3"/>
                    {lead.meeting.location_text}
                    {lead.meeting.location_lat && ` · ${lead.meeting.location_lat}, ${lead.meeting.location_lng}`}
                  </p>
                )}
                {lead.meeting.mom && (
                  <div className="mt-3 pt-3 border-t border-[#1a1a1a]">
                    <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500 mb-1">MOM</p>
                    <p className="text-xs text-gray-300 whitespace-pre-wrap">{lead.meeting.mom}</p>
                  </div>
                )}
                {lead.meeting.photos?.length > 0 && (
                  <div className="grid grid-cols-4 gap-1.5 mt-3">
                    {lead.meeting.photos.slice(0, 4).map((p, i) => (
                      <img key={`meeting-photo-${i}`} src={p} alt="" className="w-full h-14 object-cover rounded border border-[#262626]"/>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Won details */}
            {lead.won_data && (
              <div className="iz-card p-4 border-[#d4af37]/40">
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2 flex items-center gap-1">
                  <Trophy className="w-3 h-3"/> Won Packet
                </p>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><p className="text-gray-500 font-mono uppercase text-[10px]">Project</p><p className="text-white">{lead.won_data.project_name}</p></div>
                  <div><p className="text-gray-500 font-mono uppercase text-[10px]">Invoice</p><p className="text-white">{lead.won_data.invoice_name}</p></div>
                  <div><p className="text-gray-500 font-mono uppercase text-[10px]">Flat</p><p className="text-white">{lead.won_data.flat_no}</p></div>
                  <div><p className="text-gray-500 font-mono uppercase text-[10px]">City</p><p className="text-white">{lead.won_data.city}</p></div>
                  <div className="col-span-2"><p className="text-gray-500 font-mono uppercase text-[10px]">Address</p><p className="text-white">{lead.won_data.address}</p></div>
                  <div><p className="text-gray-500 font-mono uppercase text-[10px]">Project Amount</p><p className="text-[#d4af37] font-display">{fmtCurrency(lead.won_data.project_amount)}</p></div>
                  <div><p className="text-gray-500 font-mono uppercase text-[10px]">Received</p><p className="text-white font-display">{fmtCurrency(lead.won_data.amount_received)}</p></div>
                </div>

                <WonAttachments won={lead.won_data} />
              </div>
            )}

            {/* Loss info */}
            {lead.loss_data && (
              <div className="iz-card p-4 border-red-500/30">
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-red-400 mb-2 flex items-center gap-1">
                  <AlertOctagon className="w-3 h-3"/>Loss Reason
                </p>
                <p className="text-sm text-white">{lead.loss_data.reason}</p>
                {lead.loss_data.remarks && <p className="text-xs text-gray-400 mt-2 italic">&ldquo;{lead.loss_data.remarks}&rdquo;</p>}
              </div>
            )}
          </div>
        </div>
      </div>

      <MeetingModal open={meetingOpen} lead={lead} onClose={() => setMeetingOpen(false)} onSaved={() => { setMeetingOpen(false); onChanged(); }}/>
      <MeetingMomModal open={momOpen} lead={lead} onClose={() => setMomOpen(false)} onSaved={() => { setMomOpen(false); onChanged(); }}/>
      <WonGateModal open={wonOpen} lead={lead} onClose={() => setWonOpen(false)} onSaved={() => { setWonOpen(false); onChanged(); }}/>
      <LossModal open={lossOpen} lead={lead} onClose={() => setLossOpen(false)} onSaved={() => { setLossOpen(false); onChanged(); }}/>
      <EditSalesLeadDialog open={editOpen} lead={lead} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); onChanged(); }}/>
    </>
  );
}

// Renders every Won-phase attachment (floor plan PDF, initial quotation PDF, reference images)
// with click-to-open links + image thumbnails. Visible to any user (incl. Sales Executive)
// who has permission to view the won lead.
function WonAttachments({ won }) {
  if (!won) return null;
  const refImgs = Array.isArray(won.reference_images) ? won.reference_images : [];
  const nothing = !won.floor_plan_pdf && !won.initial_quotation_pdf && refImgs.length === 0;
  if (nothing) return null;

  return (
    <div className="mt-4 pt-3 border-t border-[#1a1a1a] space-y-2" data-testid="won-attachments">
      <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Attachments</p>

      {won.floor_plan_pdf && (
        <a href={won.floor_plan_pdf} target="_blank" rel="noreferrer" download="floor_plan.pdf"
           className="flex items-center gap-2 text-xs text-[#d4af37] hover:underline"
           data-testid="won-att-floor-plan">
          <FileText className="w-3.5 h-3.5"/> Floor Plan PDF
          <Download className="w-3 h-3 text-gray-500 ml-auto"/>
        </a>
      )}

      {won.initial_quotation_pdf && (
        <a href={won.initial_quotation_pdf} target="_blank" rel="noreferrer" download="initial_quotation.pdf"
           className="flex items-center gap-2 text-xs text-[#d4af37] hover:underline"
           data-testid="won-att-initial-quotation">
          <FileText className="w-3.5 h-3.5"/> Initial Quotation PDF
          <Download className="w-3 h-3 text-gray-500 ml-auto"/>
        </a>
      )}

      {refImgs.length > 0 && (
        <div>
          <p className="flex items-center gap-1 text-[11px] text-gray-400 mb-1">
            <ImageIcon className="w-3 h-3"/> Reference images ({refImgs.length})
          </p>
          <div className="grid grid-cols-4 gap-2" data-testid="won-att-refimgs">
            {refImgs.map((src, i) => (
              <a key={`ref-${i}-${(src || "").slice(-24)}`} href={src} target="_blank" rel="noreferrer" download={`reference-${i + 1}.jpg`}
                 className="block group relative" data-testid={`won-att-refimg-${i}`}>
                <img src={src} alt={`ref-${i + 1}`}
                     className="w-full h-16 object-cover rounded border border-[#262626] group-hover:border-[#d4af37]/60 transition-colors"/>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
